import { EMPLOYMENT_TYPES } from 'cpool-constants';
import type { ApplicantCreation } from 'cpool-schemas';
import type { Path } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SubTitle } from './shared/SubTitle';
import { TextInput } from 'cpool-components/input/TextInput';

import { FormDateInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDateInput';
import { FormDropdown } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDropdown';
import { FormCurrencyInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormCurrencyInput';
import { useBranchOptions, useCountryOptions } from '~/features/leadDetails/components/Overview/CustomerData/shared/useDropdownOptions';

/**
 * Component for Freiberufler and Selbstständiger employment types.
 * Both have identical form fields.
 */
export const SelfEmployed: React.FC = () => {
    const { t } = useTranslation();
    const { control, register, watch } = useFormContext<ApplicantCreation>();
    const branchOptions = useBranchOptions();
    const countryOptions = useCountryOptions();

    const beschaeftigungsart = watch('beschaeftigung.beschaeftigungsart');
    const typeKey = beschaeftigungsart === EMPLOYMENT_TYPES.FREIBERUFLER ? 'freiberufler' : 'selbststaendiger';
    const basePath = `beschaeftigung.${typeKey}` as const;

    return (
        <div className="mt-4 space-y-2">
            <FormDropdown
                id={`${basePath}.firma.branche` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.BRANCH')}
                options={branchOptions}
                control={control}
            />

            <TextInput
                id={`${basePath}.berufsbezeichnung`}
                label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.EMPLOYMENT_TYPE')}
                labelClassName="w-[175px] justify-start"
                {...register(`${basePath}.berufsbezeichnung` as Path<ApplicantCreation>)}
            />

            <FormDateInput
                id={`${basePath}.selbststaendigSeit` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.INDEPENDENT_SINCE')}
                control={control}
            />

            <SubTitle>{t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.SUBSECTIONS.INCOME_HEALTH_INSURANCE')}</SubTitle>

            <FormCurrencyInput
                id={`${basePath}.nettoeinkommenJaehrlich` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.INCOME.NET_INCOME_ANNUAL')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.bruttoEinkommenLaufendesJahr` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.INCOME.INCOME_GROSS_CURRENT_YEAR')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.bruttoEinkommenLetztesJahr` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.INCOME.INCOME_GROSS_LAST_YEAR')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.bruttoEinkommenVor2Jahren` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.INCOME.INCOME_GROSS_TWO_YEARS_AGO')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.bruttoEinkommenVor3Jahren` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.INCOME.INCOME_GROSS_THREE_YEARS_AGO')}
                control={control}
                min={0}
            />

            <SubTitle>{t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.SUBSECTIONS.EXPENSES')}</SubTitle>

            <FormCurrencyInput
                id={`${basePath}.einkommenssteuerLaufendesJahr` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EXPENSES.INCOME_TAX_CURRENT_YEAR')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.einkommenssteuerLetztesJahr` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EXPENSES.INCOME_TAX_LAST_YEAR')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.einkommenssteuerVor2Jahren` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EXPENSES.INCOME_TAX_TWO_YEARS_AGO')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.einkommenssteuerVor3Jahren` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EXPENSES.INCOME_TAX_THREE_YEARS_AGO')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.abschreibungenLaufendesJahr` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EXPENSES.DEPRECIATION_CURRENT_YEAR')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.abschreibungenLetztesJahr` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EXPENSES.DEPRECIATION_LAST_YEAR')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.abschreibungenVor2Jahren` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EXPENSES.DEPRECIATION_TWO_YEARS_AGO')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.abschreibungenVor3Jahren` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EXPENSES.DEPRECIATION_THREE_YEARS_AGO')}
                control={control}
                min={0}
            />

            <SubTitle>{t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.SUBSECTIONS.COMPANY_ADDRESS')}</SubTitle>

            <TextInput
                id={`${basePath}.firma.name`}
                label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.COMPANY_NAME')}
                labelClassName="w-[175px] justify-start"
                {...register(`${basePath}.firma.name` as Path<ApplicantCreation>)}
            />

            {/* Straße, Hausnummer */}
            <div className="flex gap-2.5 w-full justify-between items-start">
                <label className="min-w-[175px] justify-start text-sm text-midnight text-left mt-1.5">
                    {t('ADDRESS.STREET')}, {t('ADDRESS.HOUSE_NUMBER')}
                </label>
                <div className="flex flex-col w-3/4">
                    <TextInput
                        id={`${basePath}.firma.anschrift.strasse`}
                        {...register(`${basePath}.firma.anschrift.strasse` as Path<ApplicantCreation>)}
                    />
                </div>
                <div className="flex flex-col w-1/4">
                    <TextInput
                        id={`${basePath}.firma.anschrift.hausnummer`}
                        {...register(`${basePath}.firma.anschrift.hausnummer` as Path<ApplicantCreation>)}
                    />
                </div>
            </div>

            {/* PLZ, Ort */}
            <div className="flex gap-2.5 w-full justify-between items-start">
                <label className="min-w-[175px] justify-start text-sm text-midnight text-left mt-1.5">
                    {t('ADDRESS.ZIP_CODE_SHORT')}, {t('ADDRESS.LOCATION')}
                </label>
                <div className="flex flex-col w-1/4">
                    <TextInput
                        id={`${basePath}.firma.anschrift.plz`}
                        {...register(`${basePath}.firma.anschrift.plz` as Path<ApplicantCreation>)}
                    />
                </div>
                <div className="flex flex-col w-3/4">
                    <TextInput
                        id={`${basePath}.firma.anschrift.ort`}
                        {...register(`${basePath}.firma.anschrift.ort` as Path<ApplicantCreation>)}
                    />
                </div>
            </div>

            <FormDropdown
                id={`${basePath}.firma.anschrift.land` as Path<ApplicantCreation>}
                label={t('ADDRESS.COUNTRY')}
                options={countryOptions}
                control={control}
            />
        </div>
    );
};
